// Runs, for the activity feed.
//
// A log written by hand reads fine one line at a time. A log written by a board —
// or by an import, or by an agent — does not: adding twelve rows to a board is one
// act that leaves twelve events, and twelve identical lines is the shape that makes
// the whole feed unreadable, because the useful lines are buried between them.
//
// So consecutive events that are the same ACT get folded into one line you can open.
// Three rules, and all three matter:
//
//   · **Consecutive.** Never "all the item.creates today" — reordering a log to make
//     it read better is the one thing an audit trail must not do. A run is a stretch,
//     and anything that interrupts it ends it.
//   · **Same actor, same verb, same place.** Twelve rows added to one board is a run.
//     Twelve rows added across four boards is four runs, because "where" is most of
//     what the line is telling you.
//   · **Within one sitting.** A run wears one timestamp, so it must not span one. Two
//     hours apart is two sittings even with nothing in between, and a row reading
//     "16m ago" for something that started last Tuesday is simply false.

import type { CrumEvent } from './events'

/** One line in the feed: a single event, or a stretch of them folded together. */
export interface Run {
  /** stable across renders — the newest event's id, which is unique */
  id: string
  /** newest first, the order the feed reads in */
  events: CrumEvent[]
}

/** Longest gap between two events still counted as one sitting. */
const SITTING_MS = 2 * 60 * 60 * 1000

/** Below this a fold costs a click and saves nothing, so two stay two lines. */
const MIN_RUN = 3

/**
 * Fold a reverse-chronological list into runs.
 *
 * `place` answers "where did this happen" for one event — the board a row is on,
 * the record an entry is filed against. It is the caller's job because it needs the
 * projection, and this file does not take one. Returning '' means "nowhere in
 * particular", which still groups: it just groups by actor and verb alone.
 */
export function groupRuns(
  events: CrumEvent[],
  place: (ev: CrumEvent) => string,
  minRun = MIN_RUN,
): Run[] {
  const runs: Run[] = []
  let current: CrumEvent[] = []
  let key = ''

  const flush = () => {
    if (current.length === 0) return
    // A run too short to be worth folding becomes one line each, not one short fold.
    if (current.length < minRun) for (const ev of current) runs.push({ id: ev.id, events: [ev] })
    else runs.push({ id: current[0]!.id, events: current })
    current = []
  }

  for (const ev of events) {
    const k = `${ev.actor?.name ?? 'unknown'}|${ev.type}|${place(ev)}`
    // Reverse-chronological in, so the PREVIOUS event is the newer one and the gap
    // is measured backwards.
    const previous = current[current.length - 1]
    const gap = previous ? Date.parse(previous.ts) - Date.parse(ev.ts) : 0
    const sameSitting = Number.isFinite(gap) ? gap <= SITTING_MS : true
    if (k === key && sameSitting) {
      current.push(ev)
      continue
    }
    flush()
    key = k
    current = [ev]
  }
  flush()
  return runs
}
